import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useWebAndExtRouterControl} from 'src/components/micro-apps-extension/hooks/useWebAndExtRouterControl';
import type {ReactRef} from 'src/types/react';
import {useEffect} from 'react';
import {useHistory, useParams} from 'src/rerouter';
import {useDispatch} from 'react-redux';
import moment from 'moment-timezone';

import {
  Toast,
  ToastBody,
  ToastFooter,
  ToastTitle,
  toastApi,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';

import {
  getHVBroadcastData,
  setShowRenameModalPopupDetails,
} from 'src/action-creators/hv-broadcasts';


type ReturnValue = {
  isHVBroadcastLocation: boolean;
  isScheduledHVBroadcastLocation: boolean;
  showBroadcastRenamedSuccessfully: (
    id: string,
    prevName: string,
    currName: string,
  ) => void;
  showHVBroadcastSuccessfullySent: (id: string, name: string) => void;
  showHVBroadcastScheduled: (
    id: string,
    inboxId: string,
    name: string,
    date: string,
  ) => void;
  showHVBroadcastSuccessfullySaved: (id: string, name: string) => void;
  showContactListSuccessToast: (actionText: string) => void;
};

export function useHVBroadcasts(): ReturnValue {
  const {t} = useLabelTranslation();
  const router = useHistory();
  const {changeLocation} = useWebAndExtRouterControl();
  const dispatch = useDispatch();
  const location = router.location;
  // @ts-expect-error - TS2339 - Property 'pathname' does not exist on type '() => RerouterLocation'.
  const isHVBroadcastLocation = location.pathname.startsWith(
    '/messages/hv-broadcasts',
  );
  // @ts-expect-error - TS2339 - Property 'pathname' does not exist on type '() => RerouterLocation'.
  const isScheduledHVBroadcastLocation = location.pathname.startsWith(
    '/messages/hv-broadcasts/scheduled',
  );

  const showBroadcastRenamedSuccessfully = (
    id: string,
    prevName: string,
    currName: string,
  ) => {
    const onViewDetails = () => {
      changeLocation(router, `/messages/hv-broadcasts/details/${id}`);
    };
    const onDismiss = (toastId: string) => {
      toastApi.remove(toastId);
    };
    const toastId = toastApi.show(
      <Toast initialFocus={0} semantic="success">
        <ToastTitle>{`${t(
          'Broadcast_Named_Successfully',
          'Broadcast Named Successfully!',
        )}`}</ToastTitle>
        <ToastBody>{`'${prevName}' ${t(
          'renamed_to',
          'renamed to',
        )} '${currName}'`}</ToastBody>
        <ToastFooter>
          <Button onClick={() => onDismiss(toastId)}>{`${t(
            'Dismiss',
            'Dismiss',
          )}`}</Button>
          <Button onClick={onViewDetails}>{`${t(
            'View_Details',
            'View Details',
          )}`}</Button>
        </ToastFooter>
      </Toast>,
    );
  };

  const showHVBroadcastSuccessfullySent = (id: string, name: string) => {
    const onViewDetails = () => {
      changeLocation(router, `/messages/hv-broadcasts/details/${id}`);
    };
    const onDismiss = (toastId: string) => {
      toastApi.remove(toastId);
    };
    const toastId = toastApi.show(
      <Toast initialFocus={0} semantic="success">
        <ToastTitle>{`${t('Success', 'Success')}${'!'}`}</ToastTitle>
        <ToastBody>{`'${name}' ${t(
          'broadcast_sent_successfully',
          'broadcast sent successfully.',
        )}`}</ToastBody>
        <ToastFooter>
          <Button onClick={() => onDismiss(toastId)}>{`${t(
            'Dismiss',
            'Dismiss',
          )}`}</Button>
          <Button onClick={onViewDetails}>{`${t(
            'View_Details',
            'View Details',
          )}`}</Button>
        </ToastFooter>
      </Toast>,
    );
  };

  const showHVBroadcastScheduled = (
    id: string,
    inboxId: string,
    name: string,
    date: string,
  ) => {
    const onEditDetails = async () => {
      await dispatch(getHVBroadcastData(id));
      changeLocation(
        router,
        `/messages/hv-broadcasts/scheduled/${id}/from/${inboxId}`,
      );
    };
    const onDismiss = (toastId: string) => {
      toastApi.remove(toastId);
    };
    const formattedDate = `${t('Scheduled_On', 'Scheduled On')}: ${moment(
      date,
    ).format('MMM Do, YYYY, HH:mma')}`;
    const toastId = toastApi.show(
      <Toast initialFocus={0} semantic="success" time={formattedDate}>
        <ToastTitle>{`${t('Success', 'Success')}${'!'}`}</ToastTitle>
        <ToastBody>{`'${name}' ${t(
          'broadcast_scheduled_successfully',
          'broadcast scheduled successfully.',
        )}`}</ToastBody>
        <ToastFooter>
          <Button onClick={() => onDismiss(toastId)}>{`${t(
            'Dismiss',
            'Dismiss',
          )}`}</Button>
          <Button onClick={onEditDetails}>{`${t(
            'Edit_Details',
            'Edit Details',
          )}`}</Button>
        </ToastFooter>
      </Toast>,
    );
  };

  const showHVBroadcastSuccessfullySaved = (id: string, name: string) => {
    toastApi.show(
      <Toast initialFocus={0} semantic="success">
        <ToastTitle>{`${t('Success', 'Success')}!`}</ToastTitle>
        <ToastBody>{`'${name}' ${t(
          'broadcast_scheduled_successfully',
          'broadcast scheduled successfully.',
        )}`}</ToastBody>
      </Toast>,
    );
  };

  const showContactListSuccessToast = (actionText: string) => {
    toastApi.show(
      <Toast initialFocus={0} semantic="success">
        <ToastTitle>{`${t('Success', 'Success')}${'!'}`}</ToastTitle>
        <ToastBody>{`${`${t(
          'Contact_list',
          'Contact list',
        )}`} ${actionText} ${`${t(
          'successfully',
          'successfully.',
        )}`}`}</ToastBody>
      </Toast>,
    );
  };
  return {
    isHVBroadcastLocation,
    isScheduledHVBroadcastLocation,
    showBroadcastRenamedSuccessfully,
    showHVBroadcastSuccessfullySent,
    showHVBroadcastScheduled,
    showHVBroadcastSuccessfullySaved,
    showContactListSuccessToast,
  };
}
